'use client';

import { useState, useRef, useEffect } from 'react';
import { useAnalytics } from '@/hooks/use-analytics';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { LucideIcon } from 'lucide-react';

export interface FeedSortOption {
  value: string;
  label: string;
  icon?: LucideIcon;
}

interface FeedSortDropdownProps {
  options: FeedSortOption[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
}

export function FeedSortDropdown({ options, value, onChange, className }: FeedSortDropdownProps) {
  const { trackSortChange } = useAnalytics();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const active = options.find(o => o.value === value) || options[0];
  const ActiveIcon = active?.icon;

  return (
    <div ref={ref} className={cn('relative', className)}>
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 p-1.5 @md/main:px-3 @md/main:py-1.5 rounded-full text-sm font-medium bg-accent/60 hover:bg-accent text-foreground transition-colors"
      >
        {ActiveIcon && <ActiveIcon className="h-4 w-4 @md/main:h-3.5 @md/main:w-3.5" />}
        <span className="hidden @md/main:inline">{active?.label}</span>
        <ChevronDown className={cn('hidden @md/main:block h-3.5 w-3.5 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 min-w-[140px] rounded-lg border border-border bg-popover shadow-lg z-50 py-1">
          {options.map(opt => {
            const Icon = opt.icon;
            const isActive = opt.value === value;
            return (
              <button
                key={opt.value}
                onClick={() => {
                  trackSortChange(opt.value);
                  onChange(opt.value);
                  setOpen(false);
                }}
                className={cn(
                  'w-full flex items-center gap-2 px-3 py-2 text-sm transition-colors',
                  isActive
                    ? 'text-primary font-medium bg-primary/5'
                    : 'text-foreground hover:bg-accent'
                )}
              >
                {Icon && <Icon className="h-4 w-4" />}
                <span className="flex-1 text-left">{opt.label}</span>
                {isActive && <Check className="h-3.5 w-3.5 text-primary" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
